import { AgGridReact } from 'ag-grid-react';
import { memo, RefObject, useCallback, useMemo } from 'react';
import { weakEquals } from '../../utils/weakEquals';
import { BrokerNerdsBarkerCoreEnumsPricerStatus, BrokerNerdsBarkerCoreEnumsPricingMode } from '../../api';
import { PriceDisplayImpl, PriceDisplayImplProps } from '../../components/PriceDisplay/PriceDisplay';
import { useRuleState } from '../../data/Rule.state';
import { useAtomValue, useSetAtom } from 'jotai';
import { latestPendingUpdate, pendingUpdatesForListingAtom, rejectAllPendingUpdatesAtom } from '../../data/atoms';
import { TenantIdListingId } from '../../models/tenantIdListingId';
import { useGlobalState } from '../../data/Global.state';
import { useListingState } from '../../data/Listing.state';
import { PriceDisplayStaged } from '../../components/PriceDisplay/PriceDisplayStaged';

type PriceDisplayProps = {
  value: number;
  pricerStatusId: BrokerNerdsBarkerCoreEnumsPricerStatus;
  cost: number;
  listingId: string;
  tenantId: string;
  isPending: boolean;
  isAutoPriced: boolean;
  isDetailRow: boolean;
  gridRef: RefObject<AgGridReact | null>;
  isListingOnHold: boolean;
};

export const PriceDisplay = memo(({
  value,
  pricerStatusId,
  tenantId,
  listingId,
  cost,
  gridRef,
  isDetailRow,
  isListingOnHold,
}: Omit<PriceDisplayProps, 'isPending' | 'isAutoPriced'>) => {
  const { pricerMode } = useGlobalState('pricerMode');
  const { resetForm } = useRuleState('resetForm');
  const { updatePrice } = useListingState('updatePrice');
  // Subscribe only to this listing's pending updates so staging a price on one row no longer
  // re-renders every other row's price cell. See pendingUpdatesForListingAtom.
  const listingPendingUpdates = useAtomValue(pendingUpdatesForListingAtom(`${tenantId}|${listingId}`));
  const pendingPrice = latestPendingUpdate(listingPendingUpdates, 'unitPrice');
  const pendingRule = latestPendingUpdate(listingPendingUpdates, 'ruleId');
  const pendingPricerStatus = latestPendingUpdate(listingPendingUpdates, 'pricerStatusId');
  const pickPrice = useMemo(() => pendingPrice?.value ?? value, [pendingPrice?.value, value]) as number;
  const reject = useSetAtom(rejectAllPendingUpdatesAtom);
  const onCancel = useCallback<Required<PriceDisplayImplProps>['onCancel']>(() => {
    reject(TenantIdListingId.create(tenantId, listingId).toStringTyped());
  }, [tenantId, listingId, reject]);
  const { currentUser } = useGlobalState('currentUser');

  const _updatePrice = useCallback<PriceDisplayImplProps['updatePrice']>(
    async (...props) => {
      // forceUpdate=false, preserveScrollPosition=true — an inline grid price edit should leave the
      // user's scroll position alone instead of jumping back to the selected listing.
      await updatePrice(...props, false, true);
      resetForm(true);
    },
    [updatePrice, resetForm],
  );

  if (
    pricerMode === BrokerNerdsBarkerCoreEnumsPricingMode.Staged &&
    !pendingRule &&
    (!pendingPrice || pendingPrice?.inlinePriceChange) &&
    (pricerStatusId === BrokerNerdsBarkerCoreEnumsPricerStatus.None || pricerStatusId === BrokerNerdsBarkerCoreEnumsPricerStatus.Scheduled)
  ) {
    const pendingUpdate = !!pendingPrice?.inlinePriceChange;
    return (
      <PriceDisplayStaged
        key={value} // Forces component to remount when price is changed by outside factors.
        readOnly={currentUser?.roleId === 'ReadOnlyUser' || isListingOnHold}
        value={value}
        cost={cost}
        basePrice={value}
        updatePrice={_updatePrice}
        onCancel={onCancel}
        gridRef={gridRef}
        listingId={listingId}
        tenantId={tenantId}
        pricerStatusId={(pendingPricerStatus?.value as BrokerNerdsBarkerCoreEnumsPricerStatus) || pricerStatusId}
        isDetailRow={isDetailRow}
        isPending={pendingUpdate}
      />
    );
  }
  const pendingUpdate = !!pendingPrice || !!pendingRule || !!pendingPricerStatus;

  return (
    <PriceDisplayImpl
      key={pickPrice} // Note: This causes schedule pricing to remount. Comment out and use internal useEffect to fix
      readOnly={currentUser?.roleId === 'ReadOnlyUser' || isListingOnHold}
      value={pickPrice}
      cost={cost}
      basePrice={value}
      updatePrice={_updatePrice}
      onCancel={onCancel}
      gridRef={gridRef}
      listingId={listingId}
      tenantId={tenantId}
      pricerStatusId={(pendingPricerStatus?.value as BrokerNerdsBarkerCoreEnumsPricerStatus) || pricerStatusId}
      isDetailRow={isDetailRow}
      isPending={pendingUpdate}
    />
  );
}, (a, b) => weakEquals(a, b));
